import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ChevronLeft, ChevronRight, Shield, Clock, Star, Check,
  Wrench, Droplets, Settings, Zap, Wind, CircleDot, X, Calendar, Car,
  Edit2, FileText, CreditCard, Banknote, MapPin, RefreshCw, Loader, Receipt
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getActiveServices, getCarBrands, getPartsBrands, getStats, getMyInvoice } from '../api';
import { getAllFeedback, getPaymentByInvoice, savePayment } from '../store';
import './HomePage.css';

const ICON_MAP = { Wrench:<Wrench size={24}/>, Droplets:<Droplets size={24}/>, Settings:<Settings size={24}/>, Zap:<Zap size={24}/>, Wind:<Wind size={24}/>, CircleDot:<CircleDot size={24}/>, Shield:<Shield size={24}/>, Star:<Star size={24}/> };

const FALLBACK_TESTIMONIALS = [
  { id:'f1', userName:'Harpreet Singh', vehicle:'BMW 5 Series',  rating:5, review:'Exceptional service. The team handled my car with great care.' },
  { id:'f2', userName:'Priya Mehta',    vehicle:'Mercedes GLA',  rating:5, review:'Booked online, got pickup at my doorstep, and the car came back cleaner than the day I bought it.' },
  { id:'f3', userName:'Rajiv Arora',    vehicle:'Audi Q5',       rating:5, review:'Professional staff, transparent billing, real-time tracking. Will never go elsewhere.' },
];

const SERVICE_STAGES = [
  { key:'booked',    label:'Booked',     icon:'📅' },
  { key:'checkin',   label:'Check-in',   icon:'📋' },
  { key:'inservice', label:'In Service', icon:'🔧' },
  { key:'qccheck',   label:'QC Check',   icon:'✅' },
  { key:'ready',     label:'Ready',      icon:'🚗' },
];

/**
 * statusToStage — admin-set progressStage takes priority.
 * Falls back to status string mapping so it always shows something sensible.
 */
function statusToStage(status, progressStage) {
  // Backend sends progressStage (0-4) when admin advances the tracker
  if (typeof progressStage === 'number' && progressStage >= 0 && progressStage <= 4)
    return progressStage;
  const s = (status || '').toLowerCase().replace(/[_\s-]/g, '');
  if (s === 'ready')                                          return 4;
  if (s === 'qccheck' || s === 'qualitycheck' || s === 'qc') return 3;
  if (s === 'inprogress' || s === 'inservice')               return 2;
  if (s === 'confirmed')                                      return 1;
  return 0; // pending / pending_confirmation / waiting
}

const ACTIVE_STATUSES = new Set([
  'pending','pending_confirmation','PENDING_CONFIRMATION',
  'confirmed','CONFIRMED',
  'in_progress','IN_PROGRESS','in-progress','inprogress',
  'inservice','INSERVICE',
  'qc_check','QC_CHECK','qccheck',
  'ready','READY',
  'waiting','WAITING',
]);

const isActiveStatus = s => ACTIVE_STATUSES.has(s) || ACTIVE_STATUSES.has((s||'').toUpperCase());

const TIME_SLOTS = ['8:00 AM','9:00 AM','10:00 AM','11:00 AM','12:00 PM','2:00 PM','3:00 PM','4:00 PM','5:00 PM','6:00 PM'];
const inr = n => '₹' + Number(n || 0).toLocaleString('en-IN');

function Skeleton({ width='100%', height=20, radius=6, style={} }) {
  return <div style={{ width, height, borderRadius:radius, background:'linear-gradient(90deg,var(--bg-secondary) 25%,var(--border) 50%,var(--bg-secondary) 75%)', backgroundSize:'200% 100%', animation:'shimmer 1.4s infinite', flexShrink:0, ...style }}/>;
}

/* ── Active Booking Card ──────────────────────────────────── */
function ActiveBookingCard({ booking, onCancel, onReschedule, onRefresh, refreshing }) {
  // Use progressStage from backend first — this is what the admin sets
  const stage = statusToStage(booking.status, booking.progressStage);
  const pct   = Math.round((stage / (SERVICE_STAGES.length - 1)) * 100);
  const s     = (booking.status || '').toLowerCase().replace(/[_\s-]/g, '');

  const isPending   = s === 'pendingconfirmation' || s === 'pending' || s === 'waiting';
  const isConfirmed = s === 'confirmed';
  const isInService = s === 'inprogress' || s === 'inservice';
  const isQC        = s === 'qccheck' || s === 'qc' || s === 'qualitycheck';
  const isReady     = s === 'ready';

  let badge;
  if (isPending)        badge = <span className="abc-badge abc-badge--amber">⏳ Awaiting Confirmation</span>;
  else if (isConfirmed) badge = <span className="abc-badge abc-badge--blue">✓ Booking Confirmed</span>;
  else if (isInService) badge = <span className="abc-badge abc-badge--indigo">🔧 In Service</span>;
  else if (isQC)        badge = <span className="abc-badge abc-badge--purple">🔍 Quality Check</span>;
  else if (isReady)     badge = <span className="abc-badge abc-badge--green">🚗 Ready for Pickup</span>;
  else                  badge = <span className="abc-badge abc-badge--blue">🔄 Processing</span>;

  return (
    <div className="abc">
      <div className="abc__header">
        {badge}
        <div className="abc__header-right">
          <span className="abc__ref">{booking.id}</span>
          <button className="abc__refresh" onClick={onRefresh} disabled={refreshing} title="Refresh live status">
            <RefreshCw size={11} className={refreshing ? 'spin' : ''}/>
          </button>
        </div>
      </div>

      <div className="abc__vehicle">
        <div className="abc__vehicle-icon"><Settings size={16}/></div>
        <div className="abc__vehicle-info">
          <p className="abc__vehicle-name">{booking.vehicleMake} {booking.vehicleModel}{booking.vehicleYear ? ` · ${booking.vehicleYear}` : ''}</p>
          <p className="abc__vehicle-reg">{booking.regNumber}{booking.fuelType ? ` · ${booking.fuelType}` : ''}</p>
        </div>
      </div>

      <p className="abc__services">{(booking.services || []).join(' · ')}</p>
      <div className="abc__datetime"><Calendar size={11}/><span>{booking.date} at {booking.timeSlot}</span></div>

      {!isPending && (
        <div className="abc__progress-wrap">
          <div className="abc__progress-labels"><span>Progress</span><span>{pct}%</span></div>
          <div className="abc__progress-track"><div className="abc__progress-fill" style={{ width:`${pct}%` }}/></div>
        </div>
      )}

      {!isPending && (
        <div className="abc__steps">
          {SERVICE_STAGES.map((st, i) => (
            <div key={st.key} className={`abc__step${i < stage ? ' abc__step--done' : i === stage ? ' abc__step--active' : ''}`}>
              <div className="abc__step-dot">{i < stage && <Check size={5} strokeWidth={3}/>}</div>
              <span className="abc__step-emoji">{st.icon}</span>
              <span className="abc__step-label">{st.label}</span>
            </div>
          ))}
        </div>
      )}

      {isPending && (
        <div className="abc__notice abc__notice--amber">
          ⏳ Awaiting admin confirmation. We'll notify you once confirmed.
        </div>
      )}
      {isReady && (
        <div className="abc__notice abc__notice--green">
          🎉 Your vehicle is ready! Please visit the workshop or await our driver.
        </div>
      )}

      <div className="abc__actions">
        {!isReady && !isPending && (
          <button className="abc__btn abc__btn--secondary" onClick={onReschedule}><Edit2 size={12}/> Reschedule</button>
        )}
        <button className="abc__btn abc__btn--danger" onClick={onCancel}><X size={12}/> Cancel</button>
      </div>
    </div>
  );
}

/* ── Invoice Pay Card ─────────────────────────────────────── */
function InvoicePayCard({ booking, invoice, payment, onPaid }) {
  const [payStep, setPayStep] = useState('idle');
  const [paying,  setPaying]  = useState(false);
  const subtotal   = Number(invoice.subtotal  || invoice.amount || 0);
  const gstAmt     = Number(invoice.gstAmount || 0);
  const grandTotal = Number(invoice.grandTotal || subtotal + gstAmt);
  const gstPct     = invoice.gstPercent || 18;
  // Paid = backend invoice.status === 'paid' (set by admin) OR local payment record
  const isPaid     = invoice.status === 'paid' || invoice.invoicePaid || !!payment;
  const services   = (invoice.services || booking.services || []).slice(0, 3);

  const handleOnlinePay = () => {
    setPaying(true);
    setTimeout(() => {
      const pay = savePayment({ invoiceId: invoice.invoiceId||invoice.id||booking.invoiceId, bookingId: booking.id, method:'Online (UPI / Card)', amount:grandTotal, transactionId:'AE'+Date.now().toString().slice(-9), paidAt:new Date().toISOString() });
      setPaying(false); setPayStep('done'); onPaid(pay);
    }, 1400);
  };
  const handleWorkshopPay = () => {
    const pay = savePayment({ invoiceId: invoice.invoiceId||invoice.id||booking.invoiceId, bookingId: booking.id, method:'Pay at Workshop', amount:grandTotal, transactionId:'WS'+Date.now().toString().slice(-9), paidAt:new Date().toISOString() });
    setPayStep('done'); onPaid(pay);
  };

  if (payStep === 'done' || isPaid) return (
    <div className="inv-card">
      <div className="inv-card__header"><div className="inv-card__header-left"><FileText size={14} style={{color:'var(--accent)'}}/><span className="inv-card__id">{invoice.invoiceId||invoice.id||booking.invoiceId}</span></div><span className="inv-card__status-chip inv-card__status-chip--paid"><Check size={10}/> Paid</span></div>
      <div className="inv-card__vehicle"><p className="inv-card__vehicle-name">{booking.vehicleMake} {booking.vehicleModel}</p><p className="inv-card__vehicle-reg">{booking.regNumber}</p></div>
      <div className="inv-card__tags">{services.map((s,i) => <span key={i} className="inv-card__tag">{typeof s==='object'?s.name:s}</span>)}</div>
      <div className="inv-card__total-row"><span className="inv-card__total-label">Grand Total</span><span className="inv-card__total-val inv-card__total-val--paid">{inr(grandTotal)}</span></div>
      <div className="inv-card__paid-stamp"><Check size={12}/> Payment Received</div>
      <Link to="/records" className="inv-card__cta-link">View Invoice & History <ArrowRight size={12}/></Link>
    </div>
  );

  if (payStep === 'choose') return (
    <div className="inv-card">
      <div className="inv-card__header"><button className="inv-card__back-btn" onClick={() => setPayStep('idle')}><ChevronLeft size={13}/> Back</button><span className="inv-card__pill">{inr(grandTotal)}</span></div>
      <p className="inv-card__pay-heading">Choose how to pay</p>
      <button className="inv-card__pay-opt inv-card__pay-opt--primary" onClick={handleOnlinePay} disabled={paying}>
        <div className="inv-card__pay-opt-icon">{paying ? <div className="inv-card__spinner"/> : <CreditCard size={17}/>}</div>
        <div className="inv-card__pay-opt-body"><span className="inv-card__pay-opt-title">Pay Online</span><span className="inv-card__pay-opt-sub">UPI · Cards · Net Banking</span></div>
        <ArrowRight size={13} className="inv-card__pay-opt-arrow"/>
      </button>
      <button className="inv-card__pay-opt" onClick={() => setPayStep('workshop')} disabled={paying}>
        <div className="inv-card__pay-opt-icon"><Banknote size={17}/></div>
        <div className="inv-card__pay-opt-body"><span className="inv-card__pay-opt-title">Pay at Workshop</span><span className="inv-card__pay-opt-sub">Cash or card when you visit</span></div>
        <ArrowRight size={13} className="inv-card__pay-opt-arrow"/>
      </button>
    </div>
  );

  if (payStep === 'workshop') return (
    <div className="inv-card">
      <div className="inv-card__header"><button className="inv-card__back-btn" onClick={() => setPayStep('choose')}><ChevronLeft size={13}/> Back</button><span className="inv-card__pill">{inr(grandTotal)}</span></div>
      <div className="inv-card__workshop-address"><MapPin size={12}/> #24, Kissan Market, Sirhind Road, Patiala, Punjab, 147001 · Mon–Sat, 8 AM–7 PM</div>
      <p className="inv-card__workshop-note">Visit the workshop and pay at the reception. Your invoice will be ready.</p>
      <button className="btn btn-primary" style={{ width:'100%', justifyContent:'center', marginTop:4 }} onClick={handleWorkshopPay}><Check size={13}/> Confirm — I'll Pay at Workshop</button>
      <button className="inv-card__ghost-btn" onClick={() => setPayStep('choose')}>Go back</button>
    </div>
  );

  return (
    <div className="inv-card">
      <div className="inv-card__header"><div className="inv-card__header-left"><FileText size={14} style={{color:'var(--accent)'}}/><span className="inv-card__id">{invoice.invoiceId||invoice.id||booking.invoiceId}</span></div><span className="inv-card__status-chip inv-card__status-chip--unpaid">Unpaid</span></div>
      <div className="inv-card__vehicle"><p className="inv-card__vehicle-name">{booking.vehicleMake} {booking.vehicleModel}</p><p className="inv-card__vehicle-reg">{booking.regNumber}{booking.fuelType ? ` · ${booking.fuelType}` : ''}</p></div>
      <div className="inv-card__tags">{services.map((s,i) => <span key={i} className="inv-card__tag">{typeof s==='object'?s.name:s}</span>)}</div>
      <div className="inv-card__lines">
        <div className="inv-card__line"><span>Subtotal</span><span>{inr(subtotal)}</span></div>
        <div className="inv-card__line"><span>GST ({gstPct}%)</span><span>{inr(gstAmt)}</span></div>
        <div className="inv-card__line inv-card__line--grand"><span>Total Due</span><span>{inr(grandTotal)}</span></div>
      </div>
      <button className="btn btn-primary inv-card__pay-btn" onClick={() => setPayStep('choose')}><CreditCard size={13}/> Pay Now · {inr(grandTotal)}</button>
      <Link to="/records" className="inv-card__cta-link">View Full Invoice <ArrowRight size={12}/></Link>
    </div>
  );
}

/* ── Completed — no invoice yet ───────────────────────────── */
function CompletedNoInvoiceCard({ booking }) {
  return (
    <div className="completed-card">
      <div className="completed-card__icon"><Receipt size={26}/></div>
      <h3 className="completed-card__title">Service Complete! 🎉</h3>
      <p className="completed-card__msg">
        Your <strong>{booking.vehicleMake} {booking.vehicleModel}</strong> has been serviced.
        Invoice will appear once the workshop publishes it.
      </p>
      <p className="completed-card__hint">
        Go to <strong>Records</strong> to view your service history and pay your bill.
      </p>
      <Link to="/records" className="btn btn-primary" style={{ width:'100%', justifyContent:'center', marginTop:4 }}>
        <FileText size={14}/> Check Invoice &amp; Pay Bill
      </Link>
    </div>
  );
}

/* ── Modals ───────────────────────────────────────────────── */
function RescheduleModal({ booking, onClose, onSave }) {
  const [date, setDate]         = useState(booking.date || '');
  const [timeSlot, setTimeSlot] = useState(booking.timeSlot || '');
  return (
    <div className="modal-overlay animate-fade" onClick={onClose}>
      <div className="modal animate-fade-up" onClick={e => e.stopPropagation()} style={{ maxWidth:420 }}>
        <div className="modal__header"><h3 className="modal__title">Reschedule Booking</h3><button className="modal__close" onClick={onClose}><X size={18}/></button></div>
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          <div><label className="label">New Date *</label><input className="input" type="date" min={new Date().toISOString().split('T')[0]} value={date} onChange={e => setDate(e.target.value)}/></div>
          <div><label className="label">New Time Slot *</label><div className="time-slots-grid">{TIME_SLOTS.map(sl => <button key={sl} className={`time-slot ${timeSlot===sl?'selected':''}`} onClick={() => setTimeSlot(sl)}><Clock size={11}/> {sl}</button>)}</div></div>
        </div>
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:20 }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!date||!timeSlot} onClick={() => { onSave(date, timeSlot); onClose(); }}>Save Changes</button>
        </div>
      </div>
    </div>
  );
}

function CancelModal({ booking, onClose, onConfirm }) {
  return (
    <div className="modal-overlay animate-fade" onClick={onClose}>
      <div className="modal animate-fade-up" onClick={e => e.stopPropagation()} style={{ maxWidth:400 }}>
        <div className="modal__header"><h3 className="modal__title">Cancel Booking?</h3><button className="modal__close" onClick={onClose}><X size={18}/></button></div>
        <p style={{ fontSize:14, color:'var(--text-secondary)', lineHeight:1.6 }}>Are you sure you want to cancel <strong>{booking.id}</strong>?</p>
        <p style={{ fontSize:13, color:'var(--text-muted)', marginTop:8 }}>{booking.vehicleMake} {booking.vehicleModel} · {booking.services?.[0]}</p>
        <p style={{ fontSize:13, color:'var(--text-muted)', marginTop:4 }}>{booking.date} at {booking.timeSlot}</p>
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:24 }}>
          <button className="btn btn-ghost" onClick={onClose}>Keep Booking</button>
          <button className="btn btn-danger" onClick={() => { onConfirm(); onClose(); }}>Yes, Cancel It</button>
        </div>
      </div>
    </div>
  );
}

/* ── Brand logos ──────────────────────────────────────────── */
function BrandLogo({ imageUrl, abbr, color, name, size=64 }) {
  const [ok, setOk] = React.useState(true);
  React.useEffect(() => { setOk(true); }, [imageUrl]);
  return (
    <div className="brand-card-big__logo" style={{ background: imageUrl && ok ? '#fff' : color, width:size, height:size }}>
      {imageUrl && ok ? <img src={imageUrl} alt={name} className="brand-card-big__img" onLoad={() => setOk(true)} onError={() => setOk(false)}/> : <span className="brand-card-big__abbr" style={{color:'#fff'}}>{abbr}</span>}
    </div>
  );
}
function PartsBrandLogo({ imageUrl, abbr, color, bg, name }) {
  const [ok, setOk] = React.useState(true);
  React.useEffect(() => { setOk(true); }, [imageUrl]);
  return (
    <div className="parts-brand-card-big__logo" style={{ background: imageUrl && ok ? '#fff' : (bg||'#F5F5F5') }}>
      {imageUrl && ok ? <img src={imageUrl} alt={name} className="parts-brand-card-big__img" onLoad={() => setOk(true)} onError={() => setOk(false)}/> : <span className="parts-brand-card-big__abbr" style={{color}}>{abbr}</span>}
    </div>
  );
}
function ServiceCardImage({ svc }) {
  const [ok, setOk] = React.useState(true);
  const src = svc.imageUrl || `/images/services/${(svc.name||'').toLowerCase().replace(/[^a-z0-9]/g,'-')}.jpg`;
  if (!ok) return null;
  return <img src={src} alt={svc.name} className="service-card__img" onError={() => setOk(false)}/>;
}

/* ── AdSlideshow ──────────────────────────────────────────── */
const SLIDE_PATHS = ['/images/slides/slide1.jpg','/images/slides/slide2.jpg','/images/slides/slide3.jpg','/images/slides/slide4.jpg','/images/slides/slide5.jpg','/images/slides/slide6.jpg'];
const SLIDE_CAPTIONS = [
  {title:'Elite Car Service at Your Doorstep', sub:'Book online. We collect. You relax.'},
  {title:'Certified Mechanics. OEM Parts Only.', sub:'Every service backed by a 30-day warranty.'},
  {title:'Premium Detailing & Ceramic Coating', sub:'Showroom finish — guaranteed every time.'},
  {title:'Fast AC Service & Diagnostics', sub:'Same-day service for most vehicles.'},
  {title:'Wheel Alignment & Tyre Experts', sub:'Computerised 4-wheel alignment in 90 mins.'},
  {title:'Transparent Billing. No Surprises.', sub:'Full digital invoice before you pay a rupee.'},
];
function AdSlideshow() {
  const [slides, setSlides] = React.useState([]);
  const [current, setCurrent] = React.useState(0);
  const [paused, setPaused] = React.useState(false);
  const timer = React.useRef(null);
  React.useEffect(() => { setSlides(SLIDE_PATHS.map((src, i) => ({ src, caption: SLIDE_CAPTIONS[i] }))); }, []);
  React.useEffect(() => {
    if (slides.length < 2 || paused) return;
    timer.current = setInterval(() => setCurrent(c => (c+1) % slides.length), 4000);
    return () => clearInterval(timer.current);
  }, [slides.length, paused]);
  const go = d => { clearInterval(timer.current); setCurrent(c => (c+d+slides.length) % slides.length); };
  if (!slides.length) return null;
  const { src, caption } = slides[current];
  return (
    <section className="ad-slideshow" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="ad-slideshow__bg"><img key={src} src={src} alt={caption.title} className="ad-slideshow__img" onError={e => {e.target.style.display='none';}}/><div className="ad-slideshow__overlay"/></div>
      <div className="ad-slideshow__content container">
        <div className="ad-slideshow__text" key={current}>
          <p className="ad-slideshow__pretitle">AutoElite — Patiala</p>
          <h2 className="ad-slideshow__title">{caption.title}</h2>
          <p className="ad-slideshow__sub">{caption.sub}</p>
          <Link to="/booking" className="btn btn-primary ad-slideshow__cta">Book Now <ArrowRight size={16}/></Link>
        </div>
      </div>
      <button className="ad-slideshow__nav ad-slideshow__nav--prev" onClick={() => go(-1)} aria-label="Previous"><ChevronLeft size={20}/></button>
      <button className="ad-slideshow__nav ad-slideshow__nav--next" onClick={() => go(1)} aria-label="Next"><ChevronRight size={20}/></button>
      <div className="ad-slideshow__dots">{slides.map((_,i) => <button key={i} className={`ad-slideshow__dot ${i===current?'ad-slideshow__dot--active':''}`} onClick={() => { clearInterval(timer.current); setCurrent(i); }} aria-label={`Slide ${i+1}`}/>)}</div>
    </section>
  );
}

/* ── Comparison Table ─────────────────────────────────────── */
const COMPARISON_ROWS = [
  {icon:'🚗', feature:'Convenience',  label:'Pick-up & Drop',       local:'❌ Need to visit',    authorized:'❌ Need to visit',      ae:'✔️ Free pickup & drop at your doorstep'},
  {icon:'🛠️', feature:'Quality',      label:'Thorough Inspection',  local:'❌ Not guaranteed',   authorized:'❌ Basic checks only',  ae:'✔️ 40-point certified inspection'},
  {icon:'🛡️', feature:'Warranty',     label:'Service Assurance',    local:'❌ No warranty',      authorized:'✔️ Limited warranty',   ae:'✔️ 3-month service warranty'},
  {icon:'🔩', feature:'Parts',        label:'Genuine Spares',       local:'❌ Risk of fakes',    authorized:'✔️ OEM parts',          ae:'✔️ Verified genuine spare parts'},
  {icon:'⏳', feature:'Speed',        label:'Same-Day Delivery',    local:'❌ Not assured',      authorized:'❌ Multiple days',      ae:'✔️ Same-day delivery in most cases'},
  {icon:'👨‍🔧', feature:'Expertise',    label:'Skilled Mechanics',    local:'❌ Unverified',       authorized:'✔️ Trained staff',      ae:'✔️ Certified expert mechanics'},
  {icon:'🏠', feature:'Flexibility',  label:'Doorstep Servicing',   local:'❌ Not available',    authorized:'❌ Not available',      ae:'✔️ Complete doorstep service option'},
  {icon:'💰', feature:'Pricing',      label:'No Hidden Costs',      local:'❌ Price changes',    authorized:'❌ Expensive extras',   ae:'✔️ Fixed upfront pricing, no surprises'},
];
function ComparisonTable() {
  return (
    <section className="comparison-section">
      <div className="container">
        <div className="section-header">
          <div className="accent-line"/>
          <h2 className="section-title">Why Choose AutoElite?</h2>
          <p className="section-subtitle">See how we compare against local mechanics and authorised service centres.</p>
        </div>
        <div className="comparison-table-wrap">
          <table className="comparison-table">
            <thead>
              <tr>
                <th className="comparison-th comparison-th--feature">Feature</th>
                <th className="comparison-th comparison-th--local"><div className="comparison-th__inner"><span className="comparison-th__label">Local{'\n'}Mechanic</span></div></th>
                <th className="comparison-th comparison-th--auth"><div className="comparison-th__inner"><span className="comparison-th__label">Authorised{'\n'}Center</span></div></th>
                <th className="comparison-th comparison-th--ae"><div className="comparison-th__inner comparison-th__inner--ae"><span className="comparison-ae-badge">⭐ Best Choice</span><span className="comparison-th__label">AutoElite</span></div></th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON_ROWS.map((row, i) => (
                <tr key={i} className={`comparison-tr ${i%2===0?'comparison-tr--alt':''}`}>
                  <td className="comparison-td comparison-td--feature"><span className="comparison-feature-icon">{row.icon}</span><div><div className="comparison-feature-name">{row.feature}</div><div className="comparison-feature-label">{row.label}</div></div></td>
                  <td className="comparison-td comparison-td--local">{row.local}</td>
                  <td className="comparison-td comparison-td--auth">{row.authorized}</td>
                  <td className="comparison-td comparison-td--ae"><span className="comparison-ae-cell">{row.ae}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════════════════════════ */
export default function HomePage() {
  const {
    user, cancelBooking, updateBookingTime, openLogin,
    userBookings, bookingsLoading, refreshBookings,
  } = useAuth();

  const [showReschedule, setShowReschedule] = useState(false);
  const [showCancel,     setShowCancel]     = useState(false);
  const [services,     setServices]     = useState([]);
  const [carBrands,    setCarBrands]    = useState([]);
  const [partsBrands,  setPartsBrands]  = useState([]);
  const [stats,        setStats]        = useState([]);
  const [loadingData,  setLoadingData]  = useState(true);
  const [testimonials, setTestimonials] = useState(FALLBACK_TESTIMONIALS);
  const [invoiceData,  setInvoiceData]  = useState(null);
  const [paymentData,  setPaymentData]  = useState(null);
  const [refreshing,   setRefreshing]   = useState(false);

  // Track the booking ID we last fetched an invoice for — avoid refetching same booking
  const lastInvoiceBookingId = React.useRef(null);

  useEffect(() => { const fb = getAllFeedback(); if (fb?.length) setTestimonials(fb.slice(0,6)); }, []);

  useEffect(() => {
    Promise.all([getActiveServices(), getCarBrands(), getPartsBrands(), getStats()])
      .then(([svc, brands, parts, st]) => { setServices(svc); setCarBrands(brands); setPartsBrands(parts); setStats(st); setLoadingData(false); })
      .catch(() => setLoadingData(false));
  }, []);

  /* ── Derive hero booking from AuthContext's live userBookings ── */
  // AuthContext already polls every 10s — no need to duplicate the fetch.
  // We just derive from the already-fresh list.
  const heroBooking = React.useMemo(() => {
    if (!user || bookingsLoading) return undefined; // undefined = still loading
    const list = userBookings || [];
    if (!list.length) return null; // null = no bookings ever

    // 1. Active booking (any non-terminal status) — show tracker
    const active = list.find(b => isActiveStatus(b.status));
    if (active) return { ...active, _type: 'active' };

    // 2. Most recent completed — sort by createdAt desc (exact timestamp, not date string)
    const completed = list
      .filter(b => ['completed','COMPLETED'].includes(b.status))
      .sort((a, b) => {
        const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return tb - ta; // newest first
      });

    if (!completed.length) return null;
    return { ...completed[0], _type: 'completed' };
  }, [user, userBookings, bookingsLoading]);

  /* ── Fetch invoice when heroBooking is a completed booking ── */
  useEffect(() => {
    if (!heroBooking || heroBooking._type !== 'completed') {
      setInvoiceData(null);
      setPaymentData(null);
      lastInvoiceBookingId.current = null;
      return;
    }
    // Don't re-fetch if already loaded for this exact booking
    if (lastInvoiceBookingId.current === heroBooking.id && invoiceData) return;

    const fetchInvoice = async () => {
      if (!heroBooking.invoiceId) {
        // No invoice yet — show completed-no-invoice card
        setInvoiceData(null);
        setPaymentData(null);
        return;
      }
      try {
        const inv = await getMyInvoice(heroBooking.id);
        if (inv) {
          lastInvoiceBookingId.current = heroBooking.id;
          // payment status from backend invoice.status (set by admin)
          const pay = getPaymentByInvoice(inv.invoiceId || inv.id || heroBooking.invoiceId);
          setInvoiceData({
            ...inv,
            invoicePaid: inv.status === 'paid', // ensure flag is set
          });
          setPaymentData(pay || null);
        } else {
          setInvoiceData(null);
          setPaymentData(null);
        }
      } catch {
        setInvoiceData(null);
        setPaymentData(null);
      }
    };
    fetchInvoice();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, heroBooking?.id, heroBooking?.invoiceId, heroBooking?._type, invoiceData]);

  /* ── Manual refresh button — delegates to AuthContext poller ── */
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await refreshBookings();
    // Re-fetch invoice if needed
    if (heroBooking?._type === 'completed' && heroBooking?.invoiceId) {
      try {
        const inv = await getMyInvoice(heroBooking.id);
        if (inv) {
          setInvoiceData({ ...inv, invoicePaid: inv.status === 'paid' });
          const pay = getPaymentByInvoice(inv.invoiceId || inv.id);
          setPaymentData(pay || null);
        }
      } catch {}
    }
    setRefreshing(false);
  }, [refreshBookings, heroBooking]);

  /* ── Derive _type for completed booking ── */
  const resolvedHeroBooking = React.useMemo(() => {
    if (!heroBooking || heroBooking._type !== 'completed') return heroBooking;
    if (heroBooking.invoiceId && invoiceData) return { ...heroBooking, _type: 'invoice' };
    if (heroBooking.invoiceId && !invoiceData) return heroBooking; // still loading invoice
    return { ...heroBooking, _type: 'completed-no-invoice' };
  }, [heroBooking, invoiceData]);

  /* ── Hero card content ── */
  const heroCardContent = () => {
    if (!user) return (
      <div className="hero-prompt">
        <div className="hero-prompt__icon"><Car size={28}/></div>
        <h3 className="hero-prompt__title">Track Your Service</h3>
        <p className="hero-prompt__text">Sign in to see live booking status, invoices and service history.</p>
        <button className="btn btn-primary" onClick={openLogin} style={{width:'100%',justifyContent:'center',marginBottom:8}}>Sign In</button>
        <button className="btn btn-outline" onClick={openLogin} style={{width:'100%',justifyContent:'center',fontSize:12}}>Create Free Account</button>
      </div>
    );

    // Loading — AuthContext bookings not yet fetched
    if (bookingsLoading || resolvedHeroBooking === undefined) return (
      <div className="hero-prompt">
        <Loader size={28} className="spin" style={{color:'var(--accent)'}}/>
        <p style={{fontSize:13,color:'var(--text-muted)',marginTop:10}}>Loading your bookings…</p>
      </div>
    );

    if (resolvedHeroBooking?._type === 'active') return (
      <ActiveBookingCard
        booking={resolvedHeroBooking}
        onCancel={() => setShowCancel(true)}
        onReschedule={() => setShowReschedule(true)}
        onRefresh={handleRefresh}
        refreshing={refreshing}
      />
    );

    if (resolvedHeroBooking?._type === 'invoice' && invoiceData) return (
      <InvoicePayCard
        booking={resolvedHeroBooking}
        invoice={invoiceData}
        payment={paymentData}
        onPaid={pay => setPaymentData(pay)}
      />
    );

    if (resolvedHeroBooking?._type === 'completed-no-invoice') return (
      <CompletedNoInvoiceCard booking={resolvedHeroBooking}/>
    );

    // null = no bookings or still resolving invoice
    if (resolvedHeroBooking === null) return (
      <div className="hero-prompt">
        <div className="hero-prompt__icon hero-prompt__icon--success"><Check size={28}/></div>
        <h3 className="hero-prompt__title">Hi, {user.name.split(' ')[0]}!</h3>
        <p className="hero-prompt__text">No active bookings. Ready to schedule your car's next service?</p>
        <Link to="/booking" className="btn btn-primary" style={{width:'100%',justifyContent:'center'}}>Book a Service <ArrowRight size={15}/></Link>
      </div>
    );

    // Loading invoice for a completed booking
    return (
      <div className="hero-prompt">
        <Loader size={22} className="spin" style={{color:'var(--accent)'}}/>
        <p style={{fontSize:13,color:'var(--text-muted)',marginTop:10}}>Loading invoice…</p>
      </div>
    );
  };

  return (
    <div className="home">
      <style>{`@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}} @keyframes invSpin{to{transform:rotate(360deg)}} .spin{animation:invSpin 0.9s linear infinite;}`}</style>

      <section className="hero">
        <div className="hero__bg-pattern" aria-hidden="true"/>
        <img src="images/hero/hero.jpg" alt="" className="hero__bg-img" aria-hidden="true"/>
        <div className="container hero__inner">
          <div className="hero__content animate-fade-up">
            <div className="hero__eyebrow"><span className="hero__dot"/>Premium Vehicle Care · Est. 2016</div>
            <h1 className="hero__title">Your Car Deserves<br/><em>Elite Treatment</em></h1>
            <p className="hero__subtitle">Precision servicing, expert mechanics, and white-glove care for every vehicle.</p>
            <div className="hero__actions">
              <Link to="/booking" className="btn btn-primary">Book a Service <ArrowRight size={16}/></Link>
              <Link to="/services" className="btn btn-outline">Explore Services</Link>
            </div>
            <div className="hero__trust">
              <div className="hero__trust-item"><Shield size={14}/><span>Certified Mechanics</span></div>
              <div className="hero__trust-item"><Clock size={14}/><span>On-Time Delivery</span></div>
              <div className="hero__trust-item"><Star size={14}/><span>5-Star Rated</span></div>
            </div>
          </div>
          <div className="hero__visual animate-fade-up" style={{animationDelay:'0.2s'}}>
            <div className="hero__card-stack">
              <div className="hero__card hero__card--front">{heroCardContent()}</div>
            </div>
          </div>
        </div>
      </section>

      <AdSlideshow/>

      <section className="stats-bar"><div className="container"><div className="stats-bar__grid stagger">
        {loadingData
          ? [1,2,3,4].map(i => <div key={i} className="stats-bar__item"><Skeleton height={40} radius={8} style={{marginBottom:8}}/><Skeleton width="60%" height={12} radius={4} style={{margin:'0 auto'}}/></div>)
          : stats.map(({id,value,label}) => <div key={id} className="stats-bar__item animate-fade-up"><span className="stats-bar__value">{value}</span><span className="stats-bar__label">{label}</span></div>)
        }
      </div></div></section>

      <section className="brands-strip"><div className="container">
        <p className="brands-strip__label">Trusted with all major brands</p>
        {loadingData
          ? <div className="brands-strip__logos">{[1,2,3,4,5,6,7,8].map(i => <div key={i} className="brand-card-big"><Skeleton width={64} height={64} radius={12} style={{marginBottom:8}}/><Skeleton width="60%" height={11} radius={3}/></div>)}</div>
          : <div className="brands-strip__logos">{carBrands.map(b => <div key={b.id} className="brand-card-big"><BrandLogo imageUrl={b.imageUrl} abbr={b.abbr} color={b.color} name={b.name} size={64}/><span className="brand-card-big__name">{b.name}</span></div>)}</div>
        }
      </div></section>

      <section className="section"><div className="container">
        <div className="section-header"><div className="accent-line"/><h2 className="section-title">Our Services</h2><p className="section-subtitle">Comprehensive care for every make and model, delivered with surgical precision.</p></div>
        {loadingData ? (
          <div className="services-grid">{[1,2,3,4,5,6].map(i => <div key={i} className="service-card" style={{cursor:'default'}}><Skeleton height={160} radius={0} style={{margin:'-28px -24px 20px',width:'calc(100% + 48px)'}}/><Skeleton width={48} height={48} radius={8} style={{marginBottom:16}}/><Skeleton width="70%" height={18} radius={4} style={{marginBottom:10}}/><Skeleton height={14} radius={4} style={{marginBottom:6}}/><Skeleton width="85%" height={14} radius={4}/></div>)}</div>
        ) : (
          <div className="services-grid stagger">
            {services.slice(0,6).map(svc => (
              <Link key={svc.id} to="/services" className="service-card animate-fade-up">
                <div className="service-card__img-wrap"><ServiceCardImage svc={svc}/><div className="service-card__img-overlay"/></div>
                <div className="service-card__icon">{ICON_MAP[svc.icon] || <Wrench size={24}/>}</div>
                {svc.tag && <span className="service-card__tag">{svc.tag}</span>}
                <h3 className="service-card__name">{svc.name}</h3>
                <p className="service-card__desc">{svc.desc}</p>
                <div className="service-card__footer">
                  <span className="service-card__price">₹{Number(svc.price).toLocaleString('en-IN')}</span>
                  <span className="service-card__cta">Book Now <ArrowRight size={13}/></span>
                </div>
              </Link>
            ))}
          </div>
        )}
        <div style={{textAlign:'center',marginTop:40}}><Link to="/services" className="btn btn-outline">View All Services <ArrowRight size={16}/></Link></div>
      </div></section>

      <section className="how-it-works section"><div className="container">
        <div className="section-header"><div className="accent-line"/><h2 className="section-title">How It Works</h2><p className="section-subtitle">Four effortless steps to a perfect service experience.</p></div>
        <div className="how-grid stagger">
          {[{step:'01',title:'Book Online',desc:'Choose your service, date, and pickup preference online in minutes.'},{step:'02',title:'We Collect',desc:'Our driver arrives at your doorstep to collect your vehicle.'},{step:'03',title:'Expert Care',desc:'A certified mechanic is assigned. Track live progress updates.'},{step:'04',title:'Delivered Back',desc:'Car returned spotless, serviced, with a full digital report.'}].map(({step,title,desc}) => (
            <div key={step} className="how-card animate-fade-up"><span className="how-card__step">{step}</span><h3 className="how-card__title">{title}</h3><p className="how-card__desc">{desc}</p></div>
          ))}
        </div>
      </div></section>

      <ComparisonTable/>

      <section className="parts-brands-section"><div className="container">
        <div className="section-header" style={{marginBottom:32}}><div className="accent-line"/><h2 className="section-title" style={{fontSize:'clamp(1.4rem,2.5vw,1.9rem)'}}>OEM & Premium Parts Only</h2><p className="section-subtitle">We use genuine spare parts from the world's most trusted manufacturers.</p></div>
        {loadingData
          ? <div className="parts-brands-grid--big">{[1,2,3,4,5,6,7,8].map(i => <div key={i} className="parts-brand-card" style={{cursor:'default'}}><Skeleton width={40} height={40} radius={8}/><div style={{flex:1}}><Skeleton width="60%" height={14} radius={4} style={{marginBottom:5}}/><Skeleton width="80%" height={11} radius={3}/></div></div>)}</div>
          : <div className="parts-brands-grid--big">{partsBrands.map(p => <div key={p.id} className="parts-brand-card-big"><PartsBrandLogo imageUrl={p.imageUrl} abbr={p.abbr} color={p.color} bg={p.bg} name={p.name}/><div className="parts-brand-card-big__info"><p className="parts-brand-card-big__name">{p.name}</p><p className="parts-brand-card-big__desc">{p.desc}</p></div></div>)}</div>
        }
      </div></section>

      <section className="section testimonials"><div className="container">
        <div className="section-header"><div className="accent-line"/><h2 className="section-title">What Our Clients Say</h2></div>
        <div className="testimonials-grid stagger">
          {testimonials.map(t => {
            const name=t.userName||t.name||'Customer', vehicle=t.vehicle||'', rating=t.rating||5, text=t.review||t.text||'';
            return (
              <div key={t.id||name} className="testimonial-card card animate-fade-up">
                <div className="testimonial-stars">{[1,2,3,4,5].map(n => <Star key={n} size={14} fill={n<=rating?'var(--accent)':'none'} color={n<=rating?'var(--accent)':'var(--border)'}/>)}</div>
                <p className="testimonial-text">&ldquo;{text}&rdquo;</p>
                <div className="testimonial-author"><div className="testimonial-avatar--fallback">{name[0]}</div><div><p className="testimonial-name">{name}</p>{vehicle && <p className="testimonial-vehicle">{vehicle}</p>}</div></div>
              </div>
            );
          })}
        </div>
      </div></section>

      <section className="cta-banner"><div className="container"><div className="cta-banner__inner">
        <div><h2 className="cta-banner__title">Ready for Elite Treatment?</h2><p className="cta-banner__subtitle">Book your service today and experience the AutoElite difference.</p></div>
        <div className="cta-banner__actions"><Link to="/booking" className="btn btn-primary">Schedule Now <ArrowRight size={16}/></Link></div>
      </div></div></section>

      {showReschedule && resolvedHeroBooking && (
        <RescheduleModal booking={resolvedHeroBooking} onClose={() => setShowReschedule(false)} onSave={(d,ts) => updateBookingTime(resolvedHeroBooking.id, d, ts)}/>
      )}
      {showCancel && resolvedHeroBooking && (
        <CancelModal booking={resolvedHeroBooking} onClose={() => setShowCancel(false)} onConfirm={() => cancelBooking(resolvedHeroBooking.id)}/>
      )}
    </div>
  );
}